import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  createIndustrialCostDirectMaterialValuation,
  deleteIndustrialCostDirectMaterialValuation,
  getIndustrialCostDirectMaterials,
  getIndustrialCostDirectMaterialMonthlyValuations,
  getIndustrialCostDirectMaterialValuations,
  updateIndustrialCostDirectMaterialValuation,
} from '../services/industrialCostService';
import type {
  CreateDirectMaterialValuationPayload,
  DirectMaterial,
  DirectMaterialMonthlyValuation,
  DirectMaterialType,
  DirectMaterialValuation,
  UpdateDirectMaterialValuationPayload,
} from '../types/industrialCost';

import { esNumberFormat } from '../utils/numberFormat';
type FormState = {
  material_id: string;
  period_year: string;
  period_month: string;
  unit_value: string;
  is_active: boolean;
  source_reference: string;
  notes: string;
};

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;

const MONTHS = [
  { value: '1', label: 'Enero' },
  { value: '2', label: 'Febrero' },
  { value: '3', label: 'Marzo' },
  { value: '4', label: 'Abril' },
  { value: '5', label: 'Mayo' },
  { value: '6', label: 'Junio' },
  { value: '7', label: 'Julio' },
  { value: '8', label: 'Agosto' },
  { value: '9', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

const EMPTY_FORM: FormState = {
  material_id: '',
  period_year: String(CURRENT_YEAR),
  period_month: String(CURRENT_MONTH),
  unit_value: '',
  is_active: true,
  source_reference: '',
  notes: '',
};

function materialTypeLabel(type: DirectMaterialType | null | undefined) {
  if (!type) return 'Sin clasificación';
  switch (type) {
    case 'raw_material':
    case 'materia_prima':
      return 'Materia prima';
    case 'packaging':
    case 'envase':
      return 'Packaging';
    case 'ingredient':
      return 'Ingrediente';
    case 'consumable':
      return 'Consumible';
    default:
      return type;
  }
}

function formatAmount(value: number | null | undefined, digits = 2) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return esNumberFormat({
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(Number(value));
}

function formatMoney(value: number | null | undefined) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `${esNumberFormat({
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value))} €`;
}

function formatUnit(value: string | null | undefined) {
  const text = String(value ?? '').trim();
  if (!text || ['nan', 'none', 'null', 'undefined'].includes(text.toLowerCase())) return '—';
  return text;
}

function formatPeriod(year: string | number, month: string | number) {
  const monthLabel = MONTHS.find((item) => item.value === String(month))?.label;
  return `${monthLabel ?? String(month)} ${year}`;
}

function valuationStatusLabel(status: string | null | undefined) {
  switch (status) {
    case 'valid':
      return 'Completa';
    case 'warning':
      return 'Parcial';
    case 'error':
      return 'Sin valoración';
    default:
      return status || '—';
  }
}

function valuationStatusColor(status: string | null | undefined) {
  switch (status) {
    case 'valid':
      return {
        borderColor: 'rgba(34,197,94,0.45)',
        color: '#bbf7d0',
      };
    case 'warning':
      return {
        borderColor: 'rgba(245,158,11,0.55)',
        color: '#fde68a',
      };
    case 'error':
      return {
        borderColor: 'rgba(248,113,113,0.55)',
        color: '#fecaca',
      };
    default:
      return {
        borderColor: 'rgba(148,163,184,0.32)',
        color: '#cbd5e1',
      };
  }
}

function formatPriceSource(source: string | null | undefined) {
  if (source === 'calculated_pmp') return 'Calculado PMP';
  if (source === 'manual') return 'Valor manual';
  if (source === 'latest_entry') return 'Última compra';
  if (!source) return 'Sin fuente';
  return source;
}

function hasWarning(item: DirectMaterialMonthlyValuation, type: string) {
  return (item.warnings_payload ?? []).some((warning) => warning.type === type);
}

function needsOpeningValuation(item: DirectMaterialMonthlyValuation) {
  return (
    Number(item.opening_qty || 0) > 0 &&
    Number(item.opening_value || 0) <= 0 &&
    hasWarning(item, 'missing_opening_value')
  );
}

function valuationActionLabel(item: DirectMaterialMonthlyValuation) {
  if (needsOpeningValuation(item)) return 'Valorar apertura';
  return 'Valorar manual';
}

function warningLabel(type: string | null | undefined) {
  switch (type) {
    case 'missing_opening_inventory_count':
      return 'Falta apertura';
    case 'missing_closing_inventory_count':
      return 'Falta cierre';
    case 'manual_opening_value_used':
      return 'Apertura valorada';
    case 'missing_opening_value':
      return 'Falta valor apertura';
    case 'closing_stock_greater_than_available':
      return 'Cierre mayor que disponible';
    case 'manual_valuation_fallback_used':
      return 'Fallback manual';
    case 'latest_entry_price_fallback_used':
      return 'Última compra';
    case 'missing_material_valuation':
      return 'Sin valoración';
    default:
      return type || 'Warning';
  }
}

function uniqueVisibleWarnings(item: DirectMaterialMonthlyValuation) {
  const seen = new Set<string>();
  const hasOpeningValueWarning = hasWarning(item, 'missing_opening_value');

  return (item.warnings_payload ?? []).filter((warning) => {
    const type = String(warning.type ?? 'warning');

    if (seen.has(type)) return false;
    seen.add(type);

    // La fuente "Última compra" ya se comunica bajo el precio usado.
    // Si además falta valorar apertura, no debe competir visualmente con la acción principal.
    if (type === 'latest_entry_price_fallback_used' && hasOpeningValueWarning) return false;

    return true;
  });
}

function buildPayload(form: FormState): CreateDirectMaterialValuationPayload {
  return {
    material_id: form.material_id,
    period_year: Number(form.period_year),
    period_month: Number(form.period_month),
    valuation_level: 'monthly',
    valuation_source: 'manual',
    unit_value: Number(form.unit_value),
    is_active: form.is_active,
    source_reference: form.source_reference.trim() || null,
    notes: form.notes.trim() || null,
  };
}

export default function IndustrialCostDirectMaterialValuationsView() {
  const [materials, setMaterials] = useState<DirectMaterial[]>([]);
  const [valuations, setValuations] = useState<DirectMaterialValuation[]>([]);
  const [monthlyValuations, setMonthlyValuations] = useState<DirectMaterialMonthlyValuation[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>('2026');
  const [selectedMonth, setSelectedMonth] = useState<string>('4');
  const [searchText, setSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'valid' | 'warning' | 'error'>('all');
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM, period_year: '2026', period_month: '4' });
  const [manualValuationMode, setManualValuationMode] = useState<'opening' | 'fallback'>('fallback');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  async function loadData() {
    setLoading(true);
    setError(null);

    try {
      const periodYear = Number(selectedYear);
      const periodMonth = Number(selectedMonth);

      const [materialsData, valuationsData, monthlyValuationsData] = await Promise.all([
        getIndustrialCostDirectMaterials(),
        getIndustrialCostDirectMaterialValuations(),
        getIndustrialCostDirectMaterialMonthlyValuations({
          period_year: periodYear,
          period_month: periodMonth,
        }),
      ]);

      setMaterials(materialsData);
      setValuations(valuationsData);
      setMonthlyValuations(monthlyValuationsData);
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'No se pudo cargar la valoración mensual de materiales.',
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
  }, [selectedYear, selectedMonth]);

  const materialsById = useMemo(
    () => Object.fromEntries(materials.map((m) => [m.id, m])),
    [materials],
  );

  const activeMaterials = useMemo(
    () => materials.filter((m) => m.is_active),
    [materials],
  );

  const manualValuationsForPeriod = useMemo(() => {
    const year = Number(selectedYear);
    const month = Number(selectedMonth);

    return valuations.filter(
      (item) =>
        item.period_year === year &&
        item.period_month === month &&
        item.valuation_level === 'monthly' &&
        item.valuation_source === 'manual',
    );
  }, [selectedMonth, selectedYear, valuations]);

  const filteredMonthlyValuations = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    return monthlyValuations.filter((item) => {
      if (statusFilter !== 'all' && item.valuation_status !== statusFilter) return false;

      if (!search) return true;

      const haystack = `${item.material_code ?? ''} ${item.material_name ?? ''} ${item.material_type ?? ''}`.toLowerCase();
      return haystack.includes(search);
    });
  }, [monthlyValuations, searchText, statusFilter]);

  const summary = useMemo(() => {
    const total = monthlyValuations.length;
    const valid = monthlyValuations.filter((item) => item.valuation_status === 'valid').length;
    const warning = monthlyValuations.filter((item) => item.valuation_status === 'warning').length;
    const errorCount = monthlyValuations.filter((item) => item.valuation_status === 'error').length;
    const entriesValue = monthlyValuations.reduce((acc, item) => acc + Number(item.entries_value || 0), 0);
    const consumptionValue = monthlyValuations.reduce((acc, item) => {
      const price = Number(item.effective_unit_price ?? item.weighted_avg_price ?? 0);
      return acc + Number(item.consumption_qty || 0) * price;
    }, 0);

    return { total, valid, warning, errorCount, entriesValue, consumptionValue };
  }, [monthlyValuations]);

  function resetForm() {
    setForm({
      ...EMPTY_FORM,
      period_year: selectedYear,
      period_month: selectedMonth,
    });
    setManualValuationMode('fallback');
    setEditingId(null);
  }

  function startEdit(item: DirectMaterialValuation) {
    setEditingId(item.id);
    setManualValuationMode(item.source_reference === 'opening_inventory_seed' ? 'opening' : 'fallback');
    setForm({
      material_id: item.material_id,
      period_year: String(item.period_year),
      period_month: String(item.period_month),
      unit_value: String(item.unit_value),
      is_active: item.is_active,
      source_reference: item.source_reference ?? '',
      notes: item.notes ?? '',
    });
    setSuccessMessage(null);
    setError(null);
  }

  function startManualValuationForMaterial(item: DirectMaterialMonthlyValuation) {
    const isOpeningValuation = needsOpeningValuation(item);

    setManualValuationMode(isOpeningValuation ? 'opening' : 'fallback');
    setEditingId(null);
    setForm({
      material_id: item.material_id,
      period_year: selectedYear,
      period_month: selectedMonth,
      unit_value: isOpeningValuation
        ? ''
        : item.effective_unit_price
          ? String(item.effective_unit_price)
          : item.weighted_avg_price
            ? String(item.weighted_avg_price)
            : '',
      is_active: true,
      source_reference: isOpeningValuation ? 'opening_inventory_seed' : 'fallback_manual',
      notes: isOpeningValuation
        ? `Valoración de apertura para arrancar PMP en ${formatPeriod(selectedYear, selectedMonth)}.`
        : `Valoración manual creada desde valoración operativa de ${formatPeriod(selectedYear, selectedMonth)}.`,
    });
    setSuccessMessage(null);
    setError(null);
  }

  async function handleSubmit() {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const payload = buildPayload(form);

      if (!payload.material_id) {
        throw new Error('Debes seleccionar un material.');
      }
      if (!payload.period_year || !payload.period_month) {
        throw new Error('Debes informar año y mes.');
      }
      if (!payload.unit_value || payload.unit_value <= 0) {
        throw new Error('El valor unitario debe ser mayor que cero.');
      }

      if (editingId) {
        await updateIndustrialCostDirectMaterialValuation(
          editingId,
          payload as UpdateDirectMaterialValuationPayload,
        );
        setSuccessMessage('Valoración manual actualizada correctamente.');
      } else {
        await createIndustrialCostDirectMaterialValuation(payload);
        setSuccessMessage(
          manualValuationMode === 'opening'
            ? 'Valoración de apertura creada correctamente.'
            : 'Valoración manual creada correctamente.',
        );
      }

      resetForm();
      await loadData();
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'No se pudo guardar la valoración del material.',
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    const confirmed = window.confirm(
      '¿Seguro que quieres eliminar esta valoración manual de material?',
    );
    if (!confirmed) return;

    setError(null);
    setSuccessMessage(null);

    try {
      await deleteIndustrialCostDirectMaterialValuation(id);
      if (editingId === id) {
        resetForm();
      }
      setSuccessMessage('Valoración manual eliminada correctamente.');
      await loadData();
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'No se pudo eliminar la valoración del material.',
      );
    }
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography
          variant="h4"
          sx={{ fontWeight: 900, letterSpacing: '-0.6px', mb: 0.75 }}
        >
          Valoración de materiales
        </Typography>
        <Typography sx={{ color: '#94a3b8', maxWidth: 1040, lineHeight: 1.6 }}>
          Reconstruye el precio mensual de materiales con entradas, inventario y fallback manual.
          El precio puede estar disponible aunque el consumo real quede pendiente de inventario de cierre.
        </Typography>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

      {!loading && activeMaterials.length === 0 ? (
        <Alert severity="warning">
          No hay materiales directos activos. Antes de valorar materiales debes dar de alta el catálogo maestro.
        </Alert>
      ) : null}

      <Paper
        sx={{
          p: 3,
          borderRadius: 4,
          bgcolor: 'rgba(15,23,42,0.72)',
          border: '1px solid rgba(148,163,184,0.16)',
        }}
      >
        <Stack spacing={2.25}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', lg: 'flex-start' }}
          >
            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: '1rem' }}>
                Cobertura de valoración · {formatPeriod(selectedYear, selectedMonth)}
              </Typography>
              <Typography sx={{ color: '#94a3b8', mt: 0.5, maxWidth: 780 }}>
                Se muestran solo materiales relevantes para el periodo: con entradas, inventario o uso técnico en productos producidos.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <TextField
                label="Año"
                type="number"
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  setForm((prev) => ({ ...prev, period_year: e.target.value }));
                }}
                size="small"
                sx={{ minWidth: 120 }}
              />

              <TextField
                select
                label="Mes"
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  setForm((prev) => ({ ...prev, period_month: e.target.value }));
                }}
                size="small"
                sx={{ minWidth: 150 }}
              >
                {MONTHS.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(5, 1fr)',
              },
              gap: 1.5,
            }}
          >
            {[
              { label: 'Materiales relevantes', value: summary.total },
              { label: 'Completos', value: summary.valid },
              { label: 'Parciales', value: summary.warning },
              { label: 'Sin valoración', value: summary.errorCount },
              { label: 'Valor entradas del periodo', value: formatMoney(summary.entriesValue) },
            ].map((item) => (
              <Paper
                key={item.label}
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  bgcolor: 'rgba(2,6,23,0.34)',
                  border: '1px solid rgba(148,163,184,0.12)',
                }}
              >
                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800 }}>
                  {item.label}
                </Typography>
                <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '1.25rem' }}>
                  {item.value}
                </Typography>
              </Paper>
            ))}
          </Box>

          {summary.warning > 0 ? (
            <Alert severity="info" variant="outlined">
              Hay materiales con precio disponible pero reconstrucción parcial. La fuente del precio se muestra bajo el valor usado.
              Si aparece Valorar apertura, informa el precio unitario del stock inicial para cerrar el PMP correctamente.
            </Alert>
          ) : null}
        </Stack>
      </Paper>

      <Paper
        sx={{
          p: 0,
          overflow: 'hidden',
          borderRadius: 4,
          bgcolor: 'rgba(15,23,42,0.72)',
          border: '1px solid rgba(148,163,184,0.16)',
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.25,
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: 1.5,
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', lg: 'center' },
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: '1rem' }}>
              Valoración operativa del periodo
            </Typography>
            <Typography sx={{ color: '#94a3b8', mt: 0.5 }}>
              Precio usado, inventario, consumo real y estado de cada material relevante.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <TextField
              label="Buscar"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 280 } }}
            />

            <TextField
              select
              label="Estado"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              size="small"
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="valid">Completos</MenuItem>
              <MenuItem value="warning">Parciales</MenuItem>
              <MenuItem value="error">Sin valoración</MenuItem>
            </TextField>
          </Stack>
        </Box>

        <Divider sx={{ borderColor: 'rgba(148,163,184,0.12)' }} />

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Material</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Entradas</TableCell>
              <TableCell align="right">Disponible</TableCell>
              <TableCell align="right">Precio usado</TableCell>
              <TableCell align="right">Cierre</TableCell>
              <TableCell align="right">Consumo real</TableCell>
              <TableCell>Warnings</TableCell>
              <TableCell align="right">Acción</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMonthlyValuations.map((item) => {
              const unit = formatUnit(item.material_unit_label);
              const effectivePrice = item.effective_unit_price ?? item.weighted_avg_price;
              const derivedConsumptionValue =
                effectivePrice === null || effectivePrice === undefined
                  ? null
                  : Number(item.consumption_qty || 0) * Number(effectivePrice || 0);
              const visibleWarnings = uniqueVisibleWarnings(item);

              return (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <Typography sx={{ fontWeight: 900, color: '#f8fafc' }}>
                        {item.material_name ?? item.material_code ?? item.material_id}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        {item.material_code ?? 'Sin código'} · {materialTypeLabel(item.material_type)} · {unit}
                      </Typography>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={valuationStatusLabel(item.valuation_status)}
                      sx={{
                        fontWeight: 800,
                        ...valuationStatusColor(item.valuation_status),
                      }}
                    />
                  </TableCell>

                  <TableCell align="right">
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                      {formatAmount(item.entries_qty)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      {formatMoney(item.entries_value)}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                      {formatAmount(item.available_qty)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      {formatMoney(item.available_value)}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                      {formatAmount(effectivePrice)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      {formatPriceSource(item.effective_valuation_source)}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                      {formatAmount(item.closing_qty)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      {formatMoney(item.closing_value)}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                      {formatAmount(item.consumption_qty)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      {formatMoney(derivedConsumptionValue)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {visibleWarnings.length > 0 ? (
                        visibleWarnings.map((warning) => (
                          <Chip
                            key={String(warning.type ?? warning.message ?? Math.random())}
                            size="small"
                            variant="outlined"
                            label={warningLabel(String(warning.type ?? 'warning'))}
                            sx={{
                              color: '#fde68a',
                              borderColor: 'rgba(245,158,11,0.55)',
                              fontWeight: 800,
                            }}
                          />
                        ))
                      ) : (
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          —
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>

                  <TableCell align="right">
                    <Button
                      size="small"
                      variant={needsOpeningValuation(item) ? 'contained' : 'outlined'}
                      onClick={() => startManualValuationForMaterial(item)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 800,
                        ...(needsOpeningValuation(item)
                          ? {
                              bgcolor: '#38bdf8',
                              color: '#0f172a',
                              '&:hover': { bgcolor: '#38bdf8', filter: 'brightness(1.06)' },
                            }
                          : {}),
                      }}
                    >
                      {valuationActionLabel(item)}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}

            {!loading && filteredMonthlyValuations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <Alert severity="info" sx={{ my: 1 }}>
                    No hay materiales relevantes para el periodo o los filtros actuales.
                  </Alert>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Paper>

      <Paper
        sx={{
          p: 3,
          borderRadius: 4,
          bgcolor: 'rgba(15,23,42,0.72)',
          border: '1px solid rgba(148,163,184,0.16)',
        }}
      >
        <Stack spacing={2.25}>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: '1rem' }}>
              {editingId
                ? 'Editar valoración manual'
                : manualValuationMode === 'opening'
                  ? 'Valoración de apertura'
                  : 'Valoración manual del mes'}
            </Typography>
            <Typography sx={{ color: '#94a3b8', mt: 0.5 }}>
              {manualValuationMode === 'opening'
                ? 'Informa el precio unitario del stock existente al inicio del periodo. Este valor se usará para valorar la apertura y calcular el PMP mensual junto con las entradas.'
                : 'Informa un precio manual de respaldo para el mes cuando no haya PMP, entrada o valoración suficiente.'}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: '1.4fr 0.6fr 0.6fr 0.8fr',
              },
              gap: 2,
            }}
          >
            <TextField
              select
              label="Material *"
              value={form.material_id}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, material_id: e.target.value }))
              }
              fullWidth
            >
              {activeMaterials.map((material) => (
                <MenuItem key={material.id} value={material.id}>
                  {material.code} — {material.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Año *"
              type="number"
              value={form.period_year}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, period_year: e.target.value }))
              }
              fullWidth
            />

            <TextField
              select
              label="Mes *"
              value={form.period_month}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, period_month: e.target.value }))
              }
              fullWidth
            >
              {MONTHS.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label={manualValuationMode === 'opening' ? 'Precio unitario de apertura *' : 'Valor unitario *'}
              type="number"
              value={form.unit_value}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, unit_value: e.target.value }))
              }
              fullWidth
              inputProps={{ step: '0.000001', min: 0 }}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1.4fr' },
              gap: 2,
            }}
          >
            <TextField
              label="Referencia origen"
              value={form.source_reference}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  source_reference: e.target.value,
                }))
              }
              fullWidth
              helperText="Opcional en v1"
            />

            <TextField
              label="Notas"
              value={form.notes}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, notes: e.target.value }))
              }
              fullWidth
            />
          </Box>

          <Stack direction="row" spacing={1.25}>
            <Button
              variant="contained"
              onClick={() => void handleSubmit()}
              disabled={saving || loading}
              sx={{ textTransform: 'none', fontWeight: 800 }}
            >
              {saving
                ? 'Guardando...'
                : editingId
                  ? 'Guardar cambios'
                  : manualValuationMode === 'opening'
                    ? 'Guardar valoración de apertura'
                    : 'Crear valoración manual'}
            </Button>

            <Button variant="outlined" onClick={resetForm} disabled={saving} sx={{ textTransform: 'none', fontWeight: 800 }}>
              Limpiar
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        sx={{
          p: 0,
          overflow: 'hidden',
          borderRadius: 4,
          bgcolor: 'rgba(15,23,42,0.72)',
          border: '1px solid rgba(148,163,184,0.16)',
        }}
      >
        <Box sx={{ px: 3, py: 2.25 }}>
          <Typography sx={{ fontWeight: 900, fontSize: '1rem' }}>
            Valoraciones manuales registradas
          </Typography>
          <Typography sx={{ color: '#94a3b8', mt: 0.5 }}>
            Son valoraciones de respaldo. Pueden usarse como semilla de apertura o como fallback mensual.
          </Typography>
        </Box>

        <Divider sx={{ borderColor: 'rgba(148,163,184,0.12)' }} />

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Material</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Periodo</TableCell>
              <TableCell>Valor</TableCell>
              <TableCell>Uso</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Notas</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {manualValuationsForPeriod.map((item) => {
              const material = materialsById[item.material_id];
              return (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <Typography sx={{ fontWeight: 800 }}>
                        {material?.name ?? item.material_id}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        {material?.code ?? 'Sin código'}
                      </Typography>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    {material ? materialTypeLabel(material.material_type) : '—'}
                  </TableCell>

                  <TableCell>
                    {formatPeriod(item.period_year, item.period_month)}
                  </TableCell>

                  <TableCell>{formatMoney(item.unit_value)}</TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={item.source_reference === 'opening_inventory_seed' ? 'Apertura' : 'Fallback'}
                      sx={{ fontWeight: 800 }}
                    />
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      label={item.is_active ? 'Activa' : 'Inactiva'}
                      color={item.is_active ? 'success' : 'default'}
                      variant={item.is_active ? 'filled' : 'outlined'}
                    />
                  </TableCell>

                  <TableCell>{item.notes || '—'}</TableCell>

                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={() => startEdit(item)} sx={{ textTransform: 'none', fontWeight: 800 }}>
                        Editar
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => void handleDelete(item.id)}
                        sx={{ textTransform: 'none', fontWeight: 800 }}
                      >
                        Eliminar
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}

            {!loading && manualValuationsForPeriod.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Alert severity="info" sx={{ my: 1 }}>
                    Todavía no hay valoraciones manuales registradas para este periodo.
                  </Alert>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
}
